import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import {
 useVendorAnalytics,
 useVendorPerformance,
 useVendorEarnings,
 useVendorInsights,
} from '../../services/vendorAnalyticsService';
import {
 BarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 Legend,
 ResponsiveContainer,
 Line,
 AreaChart,
 Area,
 PieChart,
 Pie,
 Cell,
 RadarChart,
 Radar,
 PolarGrid,
 PolarAngleAxis,
 PolarRadiusAxis,
} from 'recharts';
import {
 BarChart3,
 Lock,
 ArrowRight,
 TrendingUp,
 DollarSign,
 Target,
 Lightbulb,
 CheckCircle,
 XCircle,
 Star,
 Users,
 Zap,
 Clock,
 ListChecks,
 Loader2,
} from 'lucide-react';
import PlatformPerformanceCard from '../../components/vendor/PlatformPerformanceCard';
import ChannelFilterTabs from '../../components/vendor/insights/ChannelFilterTabs';
import ChannelSplitCards from '../../components/vendor/insights/ChannelSplitCards';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const COLORS = ['#2563EB', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#64748B'];

const PERIOD_TABS = [
 { value: 'week', label: 'This Week' },
 { value: 'month', label: 'This Month' },
 { value: 'quarter', label: 'This Quarter' },
 { value: 'year', label: 'This Year' },
];

const VendorAdvancedAnalytics = () => {
 const { user } = useAuth();
 const navigate = useNavigate();
 const { hasActiveSubscription, isChecking, hasFeature, isLoading: loading } =
 useVendorSubscriptionFeatures('feature_analytics');
 const [period, setPeriod] = useState('month');
 const [channel, setChannel] = useState('all');

 useEffect(() => {
 if (user?.user_type !== 'vendor') {
 navigate('/');
 return;
 }
 if (hasActiveSubscription === false) {
 navigate('/vendor/subscription', { replace: true });
 }
 }, [user?.user_type, hasActiveSubscription, navigate]);

 const { data: analyticsData } = useVendorAnalytics(period, channel);
 const { data: performanceData } = useVendorPerformance(period, channel);
 const { data: earningsData } = useVendorEarnings(period, channel);
 const { data: insightsData } = useVendorInsights(period);

 const analytics = analyticsData?.data || {};
 const performance = performanceData?.data || {};
 const earnings = earningsData?.data || {};
 const insights = insightsData?.data || {};

 const formatValue = (value, formatter) => {
 if (value === null || value === undefined || isNaN(value)) return 'N/A';
 return formatter(value);
 };

 // Chart data transformations
 const revenueTrendData = (earnings.monthly_breakdown || analytics.booking_trends || []).map((item) => ({
 name: item.month || item.period,
 revenue: Number(item.net_earnings ?? item.gross_revenue ?? item.revenue ?? 0),
 bookings: Number(item.booking_count ?? 0),
 internal: Number(item.internal_bookings ?? item.internal_revenue ?? 0),
 external: Number(item.external_bookings ?? item.external_revenue ?? 0),
 }));

 const earningsByServiceData = (earnings.earnings_by_service || []).map((s) => ({
 name: (s.service_name || '').slice(0, 20),
 value: Number(s.total_earnings || 0),
 fullName: s.service_name,
 bookings: s.bookings_count || 0,
 }));

 const performanceRadarData = [
 { metric: 'Platform score', value: Number(performance.platform_performance?.score || analytics.platform_performance?.score || 0), fullMark: 100 },
 { metric: 'Completion', value: Number(performance.completion_rate || 0), fullMark: 100 },
 { metric: 'Satisfaction', value: Number(performance.client_satisfaction || 0) * 20, fullMark: 100 },
 { metric: 'Repeat Clients', value: Number(performance.repeat_client_rate || 0), fullMark: 100 },
 { metric: 'Efficiency', value: Number(performance.service_efficiency || 0), fullMark: 100 },
 { metric: 'Low Cancel', value: 100 - Number(performance.cancellation_rate || 0), fullMark: 100 },
 ];

 const peakHoursData = (performance.peak_hours || []).map((h) => ({
 name: h.hour,
 bookings: h.bookings,
 }));

 const paymentMethodsData = (earnings.payment_methods || []).map((p, i) => ({
 name: (p.payment_method || 'Other').replace(/_/g, ' '),
 value: Number(p.total_amount || 0),
 color: COLORS[i % COLORS.length],
 })).filter((d) => d.value > 0);

 if (isChecking || loading || hasActiveSubscription !== true) {
 return (
 <div className="product-panel">
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading analytics…</p>
 </div>
 </div>
 );
 }

 if (!hasFeature) {
 return (
 <div className="flex items-center justify-center py-16 px-2">
 <div className="product-panel max-w-md w-full p-8 text-center">
 <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4 border border-border">
 <Lock className="w-7 h-7 text-ink-secondary" />
 </div>
 <h1 className="text-xl font-bold text-ink mb-2">Advanced Analytics</h1>
 <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
 This feature is not included in your current plan. Upgrade to unlock charts, performance
 metrics, earnings breakdown, and insights.
 </p>
 <Link
 to="/vendor/subscription"
 className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-md font-medium hover:bg-black transition-colors"
 >
 View plans
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Advanced Analytics"
 subtitle="Charts and earnings across Internal marketplace and External private bookings."
 actions={
 <Link
 to="/vendor/analytics"
 className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-gray-900"
 >
 ← Back to basic analytics
 </Link>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.advancedAnalytics.title}
 steps={VENDOR_PAGE_GUIDES.advancedAnalytics.steps}
 icon={ListChecks}
 tone="slate"
 />

 {/* Period + channel */}
 <div className="product-panel overflow-hidden">
 <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Analytics period"
 >
 {PERIOD_TABS.map((tab) => {
 const isActive = period === tab.value;
 return (
 <button
 key={tab.value}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setPeriod(tab.value)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 </button>
 );
 })}
 </div>
 <ChannelFilterTabs value={channel} onChange={setChannel} />
 </div>
 </div>

 {channel === 'all' && (
 <ChannelSplitCards byChannel={analytics.by_channel || earnings.by_channel} />
 )}

 <PlatformPerformanceCard
 performance={performance.platform_performance || analytics.platform_performance}
 />

 {/* Summary KPI Cards */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
 {[
 {
 label: channel === 'external' ? 'Collectible revenue' : 'Total revenue',
 value: formatValue(earnings.total_earnings ?? analytics.total_revenue, (v) =>
 `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
 ),
 },
 {
 label: 'Net earnings',
 value: formatValue(earnings.net_earnings, (v) =>
 `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
 ),
 accent: true,
 },
 {
 label: 'Completion rate',
 value: formatValue(performance.completion_rate, (v) => `${Number(v).toFixed(1)}%`),
 },
 {
 label: 'Client rating',
 value: formatValue(
 performance.client_satisfaction ?? analytics.average_rating,
 (v) => `${Number(v).toFixed(1)}/5`
 ),
 },
 ].map((kpi) => (
 <div
 key={kpi.label}
 className="product-panel p-4 sm:p-5"
 >
 <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
 {kpi.label}
 </p>
 <p
 className={`mt-1.5 text-xl font-bold tracking-tight tabular-nums ${ kpi.accent ? 'text-emerald-600' : 'text-ink' }`}
 >
 {kpi.value}
 </p>
 </div>
 ))}
 </div>

 {/* Revenue & Bookings Trend Chart */}
 {revenueTrendData.length > 0 && (
 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <TrendingUp className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">
 Revenue & bookings over time
 </h2>
 </div>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <AreaChart
 data={revenueTrendData}
 margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
 >
 <defs>
 <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
 <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
 <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
 </linearGradient>
 </defs>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis dataKey="name" tick={{ fontSize: 12 }} tickFormatter={(v) => (v && v.length > 10 ? v.slice(0, 7) : v)} />
 <YAxis yAxisId="left" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
 <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
 <Tooltip
 formatter={(value, name) => [name === 'revenue' ? `$${Number(value).toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
 labelFormatter={(label) => (typeof label === 'string' ? label : String(label))}
 />
 <Legend />
 <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#2563EB" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" strokeWidth={2} />
 <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} name="Bookings" />
 </AreaChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 {/* Two-column charts */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Target className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Performance overview</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <RadarChart data={performanceRadarData}>
 <PolarGrid stroke="#E5E7EB" />
 <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
 <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
 <Radar name="Score" dataKey="value" stroke="#2563EB" fill="#2563EB" fillOpacity={0.4} strokeWidth={2} />
 <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Score']} />
 </RadarChart>
 </ResponsiveContainer>
 </div>
 </div>

 {earningsByServiceData.length > 0 && (
 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <BarChart3 className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Earnings by service</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={earningsByServiceData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
 <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 11 }} />
 <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Earnings']} />
 <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} name="Earnings" />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 {peakHoursData.length > 0 && (
 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Clock className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Peak booking hours</h2>
 </div>
 <div className="h-64">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={peakHoursData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis dataKey="name" tick={{ fontSize: 11 }} />
 <YAxis tick={{ fontSize: 11 }} />
 <Tooltip />
 <Bar dataKey="bookings" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Bookings" />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 {paymentMethodsData.length > 0 && (
 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <DollarSign className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Revenue by payment method</h2>
 </div>
 <div className="h-64">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={paymentMethodsData}
 cx="50%"
 cy="50%"
 dataKey="value"
 innerRadius={60}
 outerRadius={90}
 paddingAngle={2}
 nameKey="name"
 label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
 >
 {paymentMethodsData.map((entry, i) => (
 <Cell key={i} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, '']} />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}
 </div>

 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Target className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Detailed performance metrics</h2>
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
 {[
 {
 icon: CheckCircle,
 label: 'Completion rate',
 value: formatValue(performance.completion_rate, (v) => `${Number(v).toFixed(1)}%`),
 hint: 'Completed vs confirmed bookings',
 },
 {
 icon: XCircle,
 label: 'Cancellation rate',
 value: formatValue(performance.cancellation_rate, (v) => `${Number(v).toFixed(1)}%`),
 hint: 'Cancelled vs total bookings',
 },
 {
 icon: Star,
 label: 'Client satisfaction',
 value: formatValue(performance.client_satisfaction, (v) => `${Number(v).toFixed(1)}/5`),
 hint: 'Average review rating',
 },
 {
 icon: Users,
 label: 'Repeat client rate',
 value: formatValue(performance.repeat_client_rate, (v) => `${Number(v).toFixed(1)}%`),
 hint: 'Clients with 2+ bookings',
 },
 {
 icon: Zap,
 label: 'Service efficiency',
 value: formatValue(performance.service_efficiency, (v) => `${Number(v).toFixed(1)}%`),
 hint: 'Completed vs confirmed',
 },
 ].map((m) => {
 const Icon = m.icon;
 return (
 <div
 key={m.label}
 className="p-4 bg-surface-muted border border-border rounded-xl"
 >
 <div className="flex items-center gap-2 mb-2">
 <Icon className="w-4 h-4 text-ink-tertiary" />
 <span className="text-xs font-medium text-ink-secondary">{m.label}</span>
 </div>
 <p className="text-xl font-bold text-ink tabular-nums">{m.value}</p>
 <p className="text-xs text-ink-tertiary mt-1">{m.hint}</p>
 </div>
 );
 })}
 </div>
 </div>

 <div className="product-panel p-5 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Lightbulb className="w-5 h-5 text-ink-tertiary" />
 <h2 className="text-base font-semibold text-ink tracking-tight">Insights & recommendations</h2>
 </div>
 {insights.key_insights && insights.key_insights.length > 0 && (
 <div className="mb-6">
 <p className="text-sm font-medium text-ink-secondary mb-2">Key insights</p>
 <ul className="space-y-2">
 {insights.key_insights.map((insight, i) => (
 <li key={i} className="flex items-start gap-2 text-sm text-ink-secondary">
 <Zap className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
 {insight}
 </li>
 ))}
 </ul>
 </div>
 )}
 {insights.trends && insights.trends.length > 0 && (
 <div className="mb-6">
 <p className="text-sm font-medium text-ink-secondary mb-2">Trends</p>
 <div className="flex flex-wrap gap-2">
 {insights.trends.map((trend, i) => (
 <span
 key={i}
 className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${ trend.direction === 'up' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : trend.direction === 'down' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-gray-100 text-gray-700 border-gray-200' }`}
 >
 {typeof trend === 'string' ? trend : trend.label || trend.message || JSON.stringify(trend)}
 </span>
 ))}
 </div>
 </div>
 )}
 {insights.recommendations && insights.recommendations.length > 0 && (
 <div>
 <p className="text-sm font-medium text-ink-secondary mb-2">Recommendations</p>
 <div className="space-y-2.5">
 {insights.recommendations.map((rec, i) => (
 <div
 key={i}
 className="flex items-start gap-2.5 text-sm text-ink-secondary p-3.5 rounded-xl bg-surface-muted border border-border"
 >
 <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
 <div>
 {typeof rec === 'string' ? (
 <p>{rec}</p>
 ) : (
 <>
 {rec.title ? (
 <p className="font-medium text-ink">{rec.title}</p>
 ) : null}
 <p className={rec.title ? 'mt-0.5' : ''}>
 {rec.description || rec.message || JSON.stringify(rec)}
 </p>
 </>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 {!insights.key_insights?.length &&
 !insights.trends?.length &&
 !insights.recommendations?.length && (
 <p className="text-sm text-ink-tertiary">
 More insights appear as you complete platform bookings.
 </p>
 )}
 </div>
 </div>
 );
};

export default VendorAdvancedAnalytics;
